import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { ReteSyncPanel } from "@/components/ReteSyncPanel";
import { ReteRequestsPanel } from "@/components/ReteRequestsPanel";
import { SostanzePanel } from "@/components/SostanzePanel";
import { Upload, Plus, Trash2, Edit3, Save, X, Search, AlertTriangle } from "lucide-react";

const GROUP_LABELS = [
  "Non monogame","Neurodivergenti","Gay","BDSM/Kinky","Intersessuali",
  "Ace/Aro","Trans","Cross-dresser","Lesbiche","Bi/Pan","Disabili",
  "BIPOC","Persone grasse","Questioning","Sex workers",
];
const LEVELS = ["", "sì", "più sì che no", "più no che sì"];
const EMPTY = {
  nome: "", cognome: "", city: "", cities: [], pronomi: "", qualifica: "",
  specifiche: "", trauma: "", eta: "", lavoro_con: "", lingua: "", tempi: "",
  email: "", whatsapp: "", telegram: "", instagram: "", facebook: "", sito: "", altro: "",
  online: false, in_studio: false, agevolate: false,
  friendly_groups: [], formed_groups: {},
};

const Input = (p) => (
  <input {...p} className={`w-full rounded-lg border border-viola/20 bg-cream px-3 py-2 text-sm ${p.className || ""}`} />
);
const Textarea = (p) => (
  <textarea {...p} className={`w-full rounded-lg border border-viola/20 bg-cream px-3 py-2 text-sm ${p.className || ""}`} />
);
const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] font-semibold uppercase tracking-widest text-viola/70">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

function EditForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState({ ...EMPTY, ...initial, cities: initial?.cities || [] });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setBool = (k) => (e) => setF({ ...f, [k]: e.target.checked });

  const toggleFriendly = (g) => {
    const has = f.friendly_groups.includes(g);
    setF({ ...f, friendly_groups: has ? f.friendly_groups.filter(x => x !== g) : [...f.friendly_groups, g] });
  };
  const setFormed = (g, lvl) => {
    const copy = { ...f.formed_groups };
    if (!lvl) delete copy[g]; else copy[g] = lvl;
    setF({ ...f, formed_groups: copy });
  };
  const setCities = (v) => setF({ ...f, cities: v.split(",").map(s => s.trim()).filter(Boolean) });

  return (
    <div className="rounded-2xl bg-lilla/30 p-6 border border-viola/10" data-testid="rete-edit-form">
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Nome *"><Input value={f.nome} onChange={set("nome")} data-testid="rete-form-nome" /></Field>
        <Field label="Cognome *"><Input value={f.cognome} onChange={set("cognome")} data-testid="rete-form-cognome" /></Field>
        <Field label="Città (display)"><Input value={f.city || ""} onChange={set("city")} placeholder="es. Torino oppure «Torino e Mappano (TO)»" /></Field>
        <Field label="Città filtro (separate da virgola)">
          <Input value={(f.cities || []).join(", ")} onChange={(e) => setCities(e.target.value)} placeholder="es. Torino, Mappano" />
        </Field>
        <Field label="Pronomi"><Input value={f.pronomi || ""} onChange={set("pronomi")} /></Field>
        <Field label="Qualifica"><Input value={f.qualifica || ""} onChange={set("qualifica")} placeholder="Psicologa, Consulente sessuale" /></Field>
        <Field label="Trauma (livello)"><Input value={f.trauma || ""} onChange={set("trauma")} placeholder="sì / più sì che no / più no che sì / no" /></Field>
        <Field label="Utenza"><Input value={f.eta || ""} onChange={set("eta")} /></Field>
        <Field label="Lavoro con"><Input value={f.lavoro_con || ""} onChange={set("lavoro_con")} /></Field>
        <Field label="Lingua"><Input value={f.lingua || ""} onChange={set("lingua")} /></Field>
        <Field label="Tempi"><Input value={f.tempi || ""} onChange={set("tempi")} /></Field>
      </div>

      <div className="mt-4">
        <Field label="Approccio / Specifiche"><Textarea rows={2} value={f.specifiche || ""} onChange={set("specifiche")} /></Field>
      </div>
      <div className="mt-4">
        <Field label="Altro"><Textarea rows={2} value={f.altro || ""} onChange={set("altro")} /></Field>
      </div>

      <div className="mt-4 grid md:grid-cols-4 gap-4">
        <Field label="Email"><Input value={f.email || ""} onChange={set("email")} /></Field>
        <Field label="WhatsApp"><Input value={f.whatsapp || ""} onChange={set("whatsapp")} /></Field>
        <Field label="Telegram"><Input value={f.telegram || ""} onChange={set("telegram")} /></Field>
        <Field label="Instagram"><Input value={f.instagram || ""} onChange={set("instagram")} /></Field>
        <Field label="Facebook"><Input value={f.facebook || ""} onChange={set("facebook")} /></Field>
        <Field label="Sito"><Input value={f.sito || ""} onChange={set("sito")} /></Field>
      </div>

      <div className="mt-4 flex flex-wrap gap-4">
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.online} onChange={setBool("online")} /> Online</label>
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.in_studio} onChange={setBool("in_studio")} /> In studio</label>
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.studio_accessibile} onChange={setBool("studio_accessibile")} data-testid="admin-rete-accessibile" /> Studio accessibile</label>
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.agevolate} onChange={setBool("agevolate")} /> Tariffe agevolate</label>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-viola/70 mb-2">Friendly con</p>
          <div className="flex flex-wrap gap-1.5">
            {GROUP_LABELS.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => toggleFriendly(g)}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${f.friendly_groups.includes(g) ? "bg-viola text-cream border-viola" : "bg-cream text-viola border-viola/30 hover:bg-lilla"}`}
              >{g}</button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-viola/70 mb-2">Formata su (livello)</p>
          <div className="space-y-1.5 max-h-64 overflow-y-auto pr-2">
            {GROUP_LABELS.map((g) => (
              <div key={g} className="flex items-center gap-2">
                <span className="text-xs w-32 truncate">{g}</span>
                <select
                  value={f.formed_groups[g] || ""}
                  onChange={(e) => setFormed(g, e.target.value)}
                  className="text-xs rounded border border-viola/20 bg-cream px-2 py-1 flex-1"
                >
                  {LEVELS.map((l) => <option key={l} value={l}>{l || "—"}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 flex gap-2">
        <button
          onClick={() => onSave(f)}
          disabled={!f.nome || !f.cognome}
          className="inline-flex items-center gap-1 rounded-full bg-viola text-cream px-5 py-2 text-sm font-semibold disabled:opacity-40"
          data-testid="rete-form-save"
        >
          <Save size={14} /> Salva
        </button>
        <button onClick={onCancel} className="inline-flex items-center gap-1 rounded-full border border-viola/20 text-viola px-5 py-2 text-sm font-semibold">
          <X size={14} /> Annulla
        </button>
      </div>
    </div>
  );
}

export default function AdminRete() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);        // record or {} for new
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [confirmReplace, setConfirmReplace] = useState(null); // File
  const [importMode, setImportMode] = useState("append"); // "append" | "replace" — default safer choice
  const [syncKey, setSyncKey] = useState(0);

  const load = () => {
    http.get("/rete", { params: { q } }).then((r) => setItems(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);

  const save = async (rec) => {
    try {
      if (rec.id) {
        await http.put(`/rete/${rec.id}`, rec);
      } else {
        await http.post("/rete", rec);
      }
      setEditing(null);
      load();
    } catch (e) {
      alert("Errore nel salvataggio: " + (e.response?.data?.detail || e.message));
    }
  };
  const del = async (id) => {
    if (!confirm("Eliminare definitivamente questa scheda?")) return;
    await http.delete(`/rete/${id}`);
    load();
  };

  const doUpload = async (file) => {
    setUploading(true);
    setUploadMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await http.post(`/rete/import-excel?mode=${importMode}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data.mode === "append") {
        setUploadMsg(`✅ Aggiunti ${r.data.inserted} nuovi contatti (${r.data.skipped_duplicates} già presenti, saltati).`);
      } else {
        setUploadMsg(`✅ Sostituiti tutti i contatti: ${r.data.inserted} record importati.`);
      }
      setConfirmReplace(null);
      setSyncKey((k) => k + 1);
      load();
    } catch (e) {
      setUploadMsg("❌ Errore: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-rete">
      <ReteRequestsPanel onChanged={() => { load(); setSyncKey((k) => k + 1); }} />
      <SostanzePanel refreshKey={syncKey} />
      <ReteSyncPanel onSynced={load} refreshKey={syncKey} />
      {/* UPLOAD PANEL */}
      <div className="rounded-2xl bg-arancio/20 border border-arancio/40 p-5">
        <div className="flex items-start gap-3">
          <div className="mt-1 rounded-full bg-arancio/60 p-2 text-viola"><Upload size={18} /></div>
          <div className="flex-1">
            <p className="font-display text-lg">Importa Excel Rete GSRD</p>
            <p className="text-sm text-viola/75 mt-1">
              Il file deve seguire lo schema del foglio originale (colonne come nell'Excel di Dania).
            </p>

            {/* MODE SELECTOR */}
            <div className="mt-4 flex flex-col sm:flex-row gap-3" data-testid="rete-import-mode">
              <label className={`flex-1 rounded-xl border-2 p-3 cursor-pointer transition-colors ${importMode === "append" ? "border-fucsia bg-fucsia/10" : "border-viola/20"}`}>
                <input type="radio" name="mode" value="append" checked={importMode === "append"} onChange={() => setImportMode("append")} className="mr-2" data-testid="rete-mode-append" />
                <span className="font-semibold">➕ Aggiungi nuovi</span>
                <p className="text-xs text-viola/70 mt-1">Aggiunge solo le persone non ancora presenti (match per nome+cognome). Consigliato per aggiornare la lista.</p>
              </label>
              <label className={`flex-1 rounded-xl border-2 p-3 cursor-pointer transition-colors ${importMode === "replace" ? "border-fucsia bg-fucsia/10" : "border-viola/20"}`}>
                <input type="radio" name="mode" value="replace" checked={importMode === "replace"} onChange={() => setImportMode("replace")} className="mr-2" data-testid="rete-mode-replace" />
                <span className="font-semibold">🔄 Sostituisci tutto</span>
                <p className="text-xs text-viola/70 mt-1">Cancella l'intera lista e ricarica dal file. Utile solo per reset completo.</p>
              </label>
            </div>

            <label className="mt-4 inline-flex items-center gap-2 rounded-full bg-viola text-cream px-4 py-2 text-sm font-semibold cursor-pointer" data-testid="rete-excel-input-label">
              <Upload size={14} /> Scegli file .xlsx
              <input
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => e.target.files[0] && setConfirmReplace(e.target.files[0])}
                data-testid="rete-excel-input"
              />
            </label>
            {uploadMsg && <p className="mt-3 text-sm">{uploadMsg}</p>}
          </div>
        </div>
      </div>

      {confirmReplace && (
        <div className="rounded-xl bg-fucsia/10 border border-fucsia/40 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-fucsia mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold">
                {importMode === "append" ? "Confermi l'aggiunta?" : "Sostituire l'intera Rete?"}
              </p>
              <p className="text-xs text-viola/70 mt-1">
                File: <span className="font-mono">{confirmReplace.name}</span>.{" "}
                {importMode === "append"
                  ? "Verranno aggiunte solo le persone nuove; quelle già presenti (per nome + cognome) saranno saltate."
                  : `Tutti i ${items.length} contatti attuali saranno cancellati e sostituiti dai record del file.`}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => doUpload(confirmReplace)}
                  disabled={uploading}
                  className="rounded-full bg-fucsia text-white px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
                  data-testid="rete-excel-confirm"
                >
                  {uploading ? "Importazione…" : importMode === "append" ? "Sì, aggiungi" : "Sì, sostituisci"}
                </button>
                <button onClick={() => setConfirmReplace(null)} className="rounded-full border border-viola/20 text-viola px-4 py-1.5 text-sm">Annulla</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TOOLBAR */}
      <div className="flex flex-col sm:flex-row gap-3 items-center">
        <div className="relative flex-1 w-full">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-viola/50" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" className="pl-9" data-testid="rete-search-admin" />
        </div>
        <button
          onClick={() => setEditing({})}
          className="rounded-full bg-viola text-cream px-5 py-2 text-sm font-semibold inline-flex items-center gap-1"
          data-testid="rete-new-btn"
        >
          <Plus size={14} /> Nuova scheda
        </button>
      </div>

      {editing !== null && (
        <EditForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />
      )}

      {/* LIST TABLE */}
      <div className="rounded-2xl border border-viola/10 bg-white overflow-hidden">
        <p className="px-4 py-2 text-xs uppercase tracking-widest bg-lilla/40 text-viola/70">{items.length} risultati</p>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-viola/10 text-left">
              <th className="px-4 py-2 font-semibold">Nome</th>
              <th className="px-4 py-2 font-semibold">Città</th>
              <th className="px-4 py-2 font-semibold">Qualifica</th>
              <th className="px-4 py-2 font-semibold text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id || `${p.cognome}-${p.nome}`} className="border-b border-viola/5 hover:bg-lilla/20">
                <td className="px-4 py-2 font-semibold">{p.nome} {p.cognome}</td>
                <td className="px-4 py-2 text-viola/70">{p.city || "—"}</td>
                <td className="px-4 py-2 text-viola/70">{p.qualifica || "—"}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => setEditing(p)} className="text-viola hover:text-fucsia mr-3" title="Modifica" data-testid={`rete-edit-${p.id}`}>
                    <Edit3 size={14} />
                  </button>
                  <button onClick={() => del(p.id)} className="text-viola/60 hover:text-red-500" title="Elimina" data-testid={`rete-del-${p.id}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-viola/50">Nessunə professionistə.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
